import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Helper function to validate and format URL
export function validateAndFormatUrl(url: string): string {
  // Remove common prefixes that users might accidentally add
  let cleanedUrl = url.trim()
    .replace(/^@/, '') // Remove @ prefix
    .replace(/^https?:\/\//, '') // Remove protocol if present
    .replace(/^www\./, '') // Remove www if present
  
  // Add https:// if no protocol is specified
  if (!cleanedUrl.startsWith('http://') && !cleanedUrl.startsWith('https://')) {
    cleanedUrl = 'https://' + cleanedUrl
  }
  
  return cleanedUrl
}

// Helper function to normalize URLs
export function normalizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return ''
  }
  
  return url
    .replace(/^https?:\/\//, '') // Remove protocol
    .replace(/^www\./, '') // Remove www
    .replace(/\?.*$/, '') // Remove URL parameters
    .replace(/\/$/, ''); // Remove trailing slash
}

// Helper function to strip protocol from URLs before saving to database
// This ensures URLs are stored without http:// or https:// prefix
export function stripUrlProtocol(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') {
    return null
  }
  
  const trimmed = url.trim()
  if (!trimmed) {
    return null
  }
  
  // Remove http:// or https:// prefix
  return trimmed.replace(/^https?:\/\//i, '')
}

// Helper function to validate URL with better error handling
export function isValidUrl(url: string): boolean {
  try {
    // First clean the URL
    const cleanedUrl = validateAndFormatUrl(url)
    const urlObj = new URL(cleanedUrl)
    
    // Check if hostname has at least one dot (domain requirement)
    const hostname = urlObj.hostname
    if (!hostname.includes('.')) {
      return false
    }
    
    // Check if hostname is not just a TLD (e.g., .com, .org)
    const parts = hostname.split('.')
    if (parts.length < 2 || parts[0].length === 0) {
      return false
    }
    
    // Check if it's not just a localhost or IP address
    if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
      return false
    }
    
    return true
  } catch {
    return false
  }
}

// Enhanced URL validation with specific error messages
export function validateUrlWithError(url: string): { isValid: boolean; error?: string } {
  if (!url.trim()) {
    return { isValid: false, error: 'Please enter a website URL' }
  }
  
  try {
    const cleanedUrl = validateAndFormatUrl(url.trim())
    const urlObj = new URL(cleanedUrl)
    const hostname = urlObj.hostname
    
    // Check if hostname has at least one dot
    if (!hostname.includes('.')) {
      return { isValid: false, error: 'Please enter a valid website URL (e.g., example.com)' }
    }
    
    // Check if hostname is not just a TLD
    const parts = hostname.split('.')
    if (parts.length < 2 || parts[0].length === 0) {
      return { isValid: false, error: 'Please enter a valid website URL (e.g., example.com)' }
    }
    
    // Check if it's not localhost or IP address
    if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
      return { isValid: false, error: 'Please enter a valid website URL (e.g., example.com)' }
    }
    
    return { isValid: true }
  } catch {
    return { isValid: false, error: 'Please enter a valid website URL (e.g., example.com)' }
  }
}

// Helper function to detect URLs and emails in text and return an array of text segments and links
export function parseTextWithLinks(text: string): Array<{ type: 'text' | 'link'; content: string; url?: string }> {
  if (!text) return []
  
  interface LinkMatch {
    start: number
    end: number
    content: string
    url: string
    type: 'email' | 'url'
  }
  
  const matches: LinkMatch[] = []
  
  // Email regex pattern - matches email addresses (must come before URL pattern to avoid domain-only matches)
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+/gi
  
  // URL regex pattern - matches:
  // 1. http:// or https:// URLs
  // 2. www. URLs
  // 3. Domain patterns (e.g., example.com, subdomain.example.com)
  const urlPattern = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+(?:\/[^\s<>"']*)?)/gi
  
  // First, find all email addresses
  emailPattern.lastIndex = 0
  let emailMatch
  while ((emailMatch = emailPattern.exec(text)) !== null) {
    const email = emailMatch[0]
    matches.push({
      start: emailMatch.index,
      end: emailMatch.index + email.length,
      content: email,
      url: `mailto:${email}`,
      type: 'email'
    })
  }
  
  // Then, find all URLs (excluding those that overlap with emails)
  urlPattern.lastIndex = 0
  let urlMatch
  while ((urlMatch = urlPattern.exec(text)) !== null) {
    const url = urlMatch[0]
    const start = urlMatch.index
    const end = start + url.length
    
    // Skip if this URL overlaps with any email
    const overlapsWithEmail = matches.some(m => 
      m.type === 'email' && (
        (start >= m.start && start < m.end) ||
        (end > m.start && end <= m.end) ||
        (start <= m.start && end >= m.end)
      )
    )
    
    if (overlapsWithEmail) {
      continue
    }
    
    // Remove trailing punctuation that's likely not part of the URL
    const trailingPunctuation = url.match(/[.,;:!?]+$/)
    const cleanedUrl = url.replace(/[.,;:!?]+$/, '')
    
    let formattedUrl = cleanedUrl
    if (!cleanedUrl.startsWith('http://') && !cleanedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + cleanedUrl
    }
    
    // Only add if valid URL
    if (isValidUrl(cleanedUrl)) {
      matches.push({
        start: start,
        end: end - (trailingPunctuation ? trailingPunctuation[0].length : 0),
        content: cleanedUrl,
        url: formattedUrl,
        type: 'url'
      })
    }
  }
  
  // Sort matches by start position
  matches.sort((a, b) => a.start - b.start)
  
  // Build segments array
  const segments: Array<{ type: 'text' | 'link'; content: string; url?: string }> = []
  let lastIndex = 0
  
  for (const match of matches) {
    // Add text before this match
    if (match.start > lastIndex) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex, match.start)
      })
    }
    
    // Add the link
    segments.push({
      type: 'link',
      content: match.content,
      url: match.url
    })
    
    lastIndex = match.end
    
    // Add trailing punctuation as text if it exists (for URLs)
    if (match.type === 'url') {
      const afterMatch = text.substring(match.end)
      const trailingPunctuation = afterMatch.match(/^[.,;:!?]+/)
      if (trailingPunctuation) {
        segments.push({
          type: 'text',
          content: trailingPunctuation[0]
        })
        lastIndex = match.end + trailingPunctuation[0].length
      }
    }
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastIndex)
    })
  }
  
  // If no links found, return the whole text as a single segment
  if (segments.length === 0) {
    segments.push({
      type: 'text',
      content: text
    })
  }
  
  return segments
}
